import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import PackageStatusBadge from '../components/PackageStatusBadge';
import ProfessionalLayout from '../components/ProfessionalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  ArrowLeft, Package, Wallet, Coins, CircleDollarSign,
  AlertTriangle, Calendar, Mail, Phone, MapPin, CalendarClock,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const getInitials = (name, email) => {
  if (name) {
    const names = name.split(' ');
    if (names.length >= 2) return `${names[0][0]}${names[1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }
  return email ? email.substring(0, 2).toUpperCase() : 'U';
};

const formatDate = (dateStr, lang = 'fr') => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString(lang === 'en' ? 'en-US' : 'fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const money = (n) => `${(Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const PAYMENT_BADGE = {
  paid: 'bg-green-100 text-green-800',
  advance: 'bg-blue-100 text-blue-800',
  unpaid: 'bg-amber-100 text-amber-800',
};

export default function CollectorActivity() {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/api/admin/collectors/${partnerId}/activity`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || err.userMessage || (language === 'en' ? 'Error loading data' : 'Erreur lors du chargement'));
      } finally {
        setLoading(false);
      }
    };
    fetchActivity();
  }, [partnerId]);

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="space-y-6 p-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-28" />)}
          </div>
          <Skeleton className="h-64" />
        </div>
      </ProfessionalLayout>
    );
  }

  if (error) {
    return (
      <ProfessionalLayout>
        <div className="p-6 space-y-4">
          <Button variant="ghost" onClick={() => navigate('/team')}>
            <ArrowLeft size={16} className="mr-2" />
            {language === 'en' ? 'Back' : 'Retour'}
          </Button>
          <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
        </div>
      </ProfessionalLayout>
    );
  }

  if (!data) return null;

  const { partner, kpis, recent_packages = [], incidents = [] } = data;

  return (
    <ProfessionalLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/team')}>
            <ArrowLeft size={16} />
          </Button>
          <div className="flex items-center gap-4 flex-1">
            <Avatar className="w-12 h-12 bg-teal-600">
              <AvatarFallback className="bg-teal-600 text-white font-bold text-lg">
                {getInitials(partner.name, partner.email)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-xl font-bold">{partner.name || partner.email}</h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                {partner.email && <span className="flex items-center gap-1"><Mail size={13} /> {partner.email}</span>}
                {partner.phone && <span className="flex items-center gap-1"><Phone size={13} /> {partner.phone}</span>}
                {[partner.city, partner.country].filter(Boolean).length > 0 && (
                  <span className="flex items-center gap-1">
                    <MapPin size={13} /> {[partner.city, partner.country].filter(Boolean).join(', ')}
                  </span>
                )}
                {partner.created_at && (
                  <span className="flex items-center gap-1">
                    <Calendar size={13} /> {language === 'en' ? 'Joined' : 'Inscrit'} {formatDate(partner.created_at, language)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{language === 'en' ? 'Collected' : 'Colis collectés'}</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent><div className="text-2xl font-bold">{kpis.total_collected}</div></CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{language === 'en' ? 'This month' : 'Ce mois-ci'}</CardTitle>
              <CalendarClock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent><div className="text-2xl font-bold">{kpis.this_month}</div></CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{language === 'en' ? 'Total value' : 'Valeur totale'}</CardTitle>
              <CircleDollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent><div className="text-xl font-bold">{money(kpis.total_value)}</div></CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{language === 'en' ? 'Collected €' : 'Encaissé'}</CardTitle>
              <Wallet className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent><div className="text-xl font-bold text-green-600">{money(kpis.amount_paid)}</div></CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{language === 'en' ? 'Outstanding' : 'Reste à encaisser'}</CardTitle>
              <Coins className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent><div className="text-xl font-bold text-amber-600">{money(kpis.amount_pending)}</div></CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{language === 'en' ? 'Open incidents' : 'Incidents ouverts'}</CardTitle>
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent><div className="text-2xl font-bold">{kpis.open_incidents}</div></CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="packages">
          <TabsList className="flex flex-wrap h-auto gap-1">
            <TabsTrigger value="packages">
              {language === 'en' ? 'Packages' : 'Colis collectés'} ({recent_packages.length})
            </TabsTrigger>
            <TabsTrigger value="incidents">
              {language === 'en' ? 'Incidents' : 'Incidents'} ({incidents.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="packages">
            <Card>
              <CardContent className="p-0">
                {recent_packages.length === 0 ? (
                  <p className="text-muted-foreground text-sm p-6">{language === 'en' ? 'No packages collected.' : 'Aucun colis collecté.'}</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'en' ? 'OCE Code' : 'Code OCE'}</TableHead>
                        <TableHead>{t('recipient')}</TableHead>
                        <TableHead>{language === 'en' ? 'Destination' : 'Destination'}</TableHead>
                        <TableHead className="text-right">{language === 'en' ? 'Price' : 'Prix'}</TableHead>
                        <TableHead>{language === 'en' ? 'Payment' : 'Paiement'}</TableHead>
                        <TableHead>{language === 'en' ? 'Status' : 'Statut'}</TableHead>
                        <TableHead>{language === 'en' ? 'Date' : 'Date'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recent_packages.map((p, idx) => (
                        <TableRow key={p.id || idx}>
                          <TableCell className="font-mono text-sm">{p.oce_code || '—'}</TableCell>
                          <TableCell>{p.recipient_name || '—'}</TableCell>
                          <TableCell className="text-sm">
                            {[p.destination_city, p.destination_country].filter(Boolean).join(', ') || '—'}
                          </TableCell>
                          <TableCell className="text-right font-medium">{money(p.price)}</TableCell>
                          <TableCell>
                            <Badge className={`${PAYMENT_BADGE[p.payment_status] || PAYMENT_BADGE.unpaid} text-xs`}>
                              {p.payment_status || 'unpaid'}
                            </Badge>
                          </TableCell>
                          <TableCell><PackageStatusBadge status={p.status} size="sm" /></TableCell>
                          <TableCell className="text-sm">{formatDate(p.created_at, language)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="incidents">
            <Card>
              <CardContent className="p-0">
                {incidents.length === 0 ? (
                  <p className="text-muted-foreground text-sm p-6">{language === 'en' ? 'No incidents reported.' : 'Aucun incident signalé.'}</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'en' ? 'Category' : 'Catégorie'}</TableHead>
                        <TableHead>{language === 'en' ? 'Status' : 'Statut'}</TableHead>
                        <TableHead>{language === 'en' ? 'OCE Code' : 'Code OCE'}</TableHead>
                        <TableHead>{language === 'en' ? 'Date' : 'Date'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {incidents.map((inc) => (
                        <TableRow key={inc.id}>
                          <TableCell>{inc.category || '—'}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{inc.status}</Badge></TableCell>
                          <TableCell className="font-mono text-sm">{inc.package_oce_code || '—'}</TableCell>
                          <TableCell className="text-sm">{formatDate(inc.created_at, language)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </ProfessionalLayout>
  );
}
